import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { useAsyncJob } from "@/composables/useAsyncJob";
import useMyFetch from "@stores/useMyFetch";
import { getClientId } from "@utils/clientId";
import {
  callsProgrammingCheck,
  renderActivitySteps,
  summarizeActivity,
  type ActivityStep,
  type RenderedStep,
} from "@utils/assistantActivity";
import type { AssistantScope } from "@/composables/useAssistantScope";

interface AssistantMessage {
  id: string;
  role: "user" | "assistant" | "error";
  content: string;
  timestamp: Date;
  steps?: RenderedStep[];
  stepsSummary?: string;
  feedback?: string | null;
  files?: AssistantFile[];
  question?: AssistantQuestionForm | null;
  programmingCheck?: boolean;
}

export interface AssistantQuestion {
  question: string;
  options: string[];
  multiple?: boolean;
}

export type AssistantAnswer = string | string[];

/** The `ask_user` form: its questions, and the answers once they were sent. */
export interface AssistantQuestionForm {
  questions: AssistantQuestion[];
  answers: Record<string, AssistantAnswer> | null;
}

interface AssistantFile {
  name: string;
  // Absent until the conversation is reloaded from the server, since the
  // attachment ids only exist once the message is persisted.
  url?: string;
}

interface ConversationSummary {
  token: string;
  title: string;
  updated_at: string;
  scope: string;
}

/** The scope's own description, served by the API so it is defined once. */
interface ScopeDefinition {
  key: string;
  mode: "read" | "write";
  verb: string;
  target: string;
  context_type: string | null;
  filters: boolean;
  suggestions: Record<string, string[]>;
}

interface Turn {
  content: string;
  files: File[];
  target: AssistantScope;
  cable: unknown;
  vueInstance: unknown;
  // Tells the API to reuse the question already in the thread instead of
  // persisting the same message twice.
  retried?: boolean;
}

interface CableMessage {
  action: string;
  text?: string;
  message?: string;
  name?: string;
  kind?: string;
  detail?: string;
  ref?: string;
  args?: string;
  at?: number;
  done_at?: number;
  message_id?: number;
  mutated_tools?: string[];
  questions?: AssistantQuestion[];
}

export const useAssistantStore = defineStore("assistant", () => {
  const open = ref(false);
  // An answer that landed while the panel was closed, so the launcher can say so.
  const hasUnread = ref(false);
  const scope = ref<AssistantScope | null>(null);
  // Which scope the store is already bound to, so binding it again is a no-op.
  const boundKey = ref<string | null>(null);
  const scopeDefinitions = ref<ScopeDefinition[]>([]);
  const conversations = ref<ConversationSummary[]>([]);
  const currentConversationToken = ref<string | null>(null);
  const messages = ref<AssistantMessage[]>([]);
  const isStreaming = ref(false);
  const streamingContent = ref("");
  // Kept raw so a tool call's arguments and duration can enrich the step
  // already displayed.
  const rawSteps = ref<ActivityStep[]>([]);
  const streamingSteps = computed(() => renderActivitySteps(rawSteps.value, true));
  const streamingSummary = computed(() => summarizeActivity(rawSteps.value));
  // Set mid-stream by the ask_user tool, attached to the answer once it lands.
  const pendingQuestion = ref<AssistantQuestionForm | null>(null);
  const currentClientId = ref<string | null>(null);
  // What Retry replays after an error.
  const lastTurn = ref<Turn | null>(null);
  // A prompt handed over by the page, waiting for the host to send it.
  const queuedPrompt = ref<string | null>(null);
  // Bumped when a turn wrote something, so the page being viewed can reload.
  // The tool names say *what* changed, which decides which store to refresh.
  const mutatedAt = ref(0);
  const mutatedTools = ref<string[]>([]);

  const scopeDefinition = computed(
    () => scopeDefinitions.value.find((definition) => definition.key === scope.value?.key) ?? null,
  );
  const suggestionGroups = computed(() => scopeDefinition.value?.suggestions ?? {});
  const currentTitle = computed(
    () =>
      conversations.value.find((conversation) => conversation.token === currentConversationToken.value)?.title ??
      "New conversation",
  );

  const { execute } = useAsyncJob();

  async function loadScopeDefinitions() {
    if (scopeDefinitions.value.length) return;

    const { data } = await useMyFetch("/assistant/scopes").json<{ scopes: ScopeDefinition[] }>();
    scopeDefinitions.value = data.value?.scopes ?? [];
  }

  function toggle(force?: boolean) {
    open.value = force ?? !open.value;
    if (open.value) hasUnread.value = false;
  }

  // A page can hand the panel a prompt to start from. The host owns the cable,
  // so it is what actually sends it.
  function openWith(prompt: string) {
    if (isStreaming.value) return;

    toggle(true);
    queuedPrompt.value = prompt;
  }

  function takeQueuedPrompt() {
    const prompt = queuedPrompt.value;
    queuedPrompt.value = null;
    return prompt;
  }

  const launcherState = computed(() => {
    if (isStreaming.value) return "working";
    return hasUnread.value ? "done" : "idle";
  });

  // A conversation belongs to the page it was opened from: moving to another
  // scope, or to another survey, closes the panel and drops the thread rather
  // than carrying it somewhere its tools no longer apply. Binding the scope
  // already bound changes nothing, so the Episto AI page can bind its own before
  // restoring a thread from the URL, and the host mounting after it is inert.
  function bindScope(next: AssistantScope | null) {
    const key = next ? `${next.key}:${next.contextToken ?? ""}` : null;
    if (key === boundKey.value) return;

    boundKey.value = key;
    // A turn left running would keep streaming into a panel that no longer
    // shows its page, so it is dropped: its client id stops being the current
    // one, which is what makes its remaining broadcasts ignored.
    if (isStreaming.value) stopGeneration();

    scope.value = next;
    open.value = false;
    hasUnread.value = false;
    conversations.value = [];
    currentConversationToken.value = null;
    currentClientId.value = null;
    messages.value = [];
    lastTurn.value = null;
    resetStreamingState();
    if (next) loadConversations();
  }

  function addMessage(message: Pick<AssistantMessage, "role" | "content"> & Partial<AssistantMessage>) {
    const { id, ...rest } = message;
    messages.value.push({ id: id ?? crypto.randomUUID(), timestamp: new Date(), feedback: null, ...rest });
  }

  // The backend appends a filename marker to the persisted content so replayed
  // history keeps a trace of the attachment; the chips already show it here.
  function withoutFilesMarker(content: string) {
    return content.replace(/\n*\[Attached files:[^\]]*\]\s*$/, "");
  }

  function resetStreamingState() {
    streamingContent.value = "";
    rawSteps.value = [];
    pendingQuestion.value = null;
    isStreaming.value = false;
  }

  async function loadConversations() {
    if (!scope.value) return;

    const query = new URLSearchParams({ scope: scope.value.key });
    if (scope.value.contextType && scope.value.contextToken) {
      query.append("context_type", scope.value.contextType);
      query.append("context_token", scope.value.contextToken);
    }

    const { data } = await useMyFetch(`/assistant_conversations?${query}`).json<{
      conversations: ConversationSummary[];
    }>();
    conversations.value = data.value?.conversations ?? [];
  }

  // Returns false when the thread could not be read, so a caller restoring it
  // from the URL can drop the token it came from.
  async function openConversation(token: string) {
    if (isStreaming.value) return false;

    const { data } = await useMyFetch(`/assistant_conversations/${token}`).json<{
      token: string;
      messages: {
        id: number;
        role: "user" | "assistant";
        content: string;
        user_feedback: string | null;
        activity_steps: ActivityStep[] | null;
        files: AssistantFile[];
        question: AssistantQuestionForm | null;
        created_at: string;
      }[];
    }>();
    if (!data.value) return false;

    currentConversationToken.value = token;
    lastTurn.value = null;
    resetStreamingState();
    messages.value = data.value.messages.map((message) => ({
      id: String(message.id),
      role: message.role,
      content: message.role === "user" ? withoutFilesMarker(message.content) : message.content,
      timestamp: new Date(message.created_at),
      feedback: message.user_feedback,
      steps: renderActivitySteps(message.activity_steps),
      stepsSummary: summarizeActivity(message.activity_steps),
      files: message.files,
      question: message.question,
      programmingCheck: callsProgrammingCheck(message.activity_steps),
    }));
    return true;
  }

  function newConversation() {
    if (isStreaming.value) return;

    currentConversationToken.value = null;
    messages.value = [];
    lastTurn.value = null;
    resetStreamingState();
  }

  async function deleteConversation(token: string) {
    await useMyFetch(`/assistant_conversations/${token}`).delete();
    conversations.value = conversations.value.filter((conversation) => conversation.token !== token);
    if (currentConversationToken.value === token) newConversation();
  }

  // The list holds the title the header reads through currentTitle, so patching
  // it in place is enough for both surfaces to follow.
  async function renameConversation(token: string, title: string) {
    const conversation = conversations.value.find((candidate) => candidate.token === token);
    const renamed = title.trim();
    if (!conversation || !renamed || renamed === conversation.title) return;

    const { data, response } = await useMyFetch(`/assistant_conversations/${token}`)
      .patch({ assistant_conversation: { title: renamed } })
      .json<ConversationSummary>();

    if (response.value?.ok) conversation.title = data.value?.title ?? renamed;
  }

  // Files force a multipart body; useMyFetch keeps injecting the CSRF header
  // either way, as long as FormData is passed through unserialised. Context
  // travels as two flat params so both body shapes carry it identically.
  function chatBody(turn: Turn, clientId: string) {
    const { content: message, files, target } = turn;

    if (!files.length) {
      return {
        message,
        client_id: clientId,
        cable_client_id: getClientId(),
        browser_language: navigator.language,
        conversation_token: currentConversationToken.value,
        scope: target.key,
        context_type: target.contextType,
        context_token: target.contextToken,
        page_filters: target.pageFilters,
        retry: turn.retried ?? false,
      };
    }

    const body = new FormData();
    body.append("message", message);
    body.append("client_id", clientId);
    body.append("cable_client_id", getClientId());
    body.append("browser_language", navigator.language);
    body.append("scope", target.key);
    if (currentConversationToken.value) body.append("conversation_token", currentConversationToken.value);
    if (target.contextType && target.contextToken) {
      body.append("context_type", target.contextType);
      body.append("context_token", target.contextToken);
    }
    Object.entries(target.pageFilters).forEach(([key, value]) =>
      Array.isArray(value)
        ? value.forEach((item) => body.append(`page_filters[${key}][]`, item))
        : body.append(`page_filters[${key}]`, value),
    );
    files.forEach((file) => body.append("files[]", file));
    return body;
  }

  async function postChat(turn: Turn, clientId: string) {
    const { response, data } = await useMyFetch("/assistant/chat")
      .post(chatBody(turn, clientId))
      .json<{ conversation_token: string }>();
    return { ok: response.value?.ok, conversationToken: data.value?.conversation_token };
  }

  async function sendMessage(
    content: string,
    cable: unknown,
    vueInstance: unknown,
    files: File[] = [],
    target: AssistantScope | null = null,
  ) {
    const sendScope = target ?? scope.value;
    if (!content.trim() || isStreaming.value || !sendScope) return;

    addMessage({ role: "user", content, files: files.map((file) => ({ name: file.name })) });
    await runTurn({ content, files, target: sendScope, cable, vueInstance });
  }

  // Replays the turn the error interrupted, without a second user bubble: the
  // question is already in the thread, only the failed answer is dropped.
  async function retryLastTurn() {
    const turn = lastTurn.value;
    if (!turn || isStreaming.value) return;

    messages.value = messages.value.filter((message) => message.role !== "error");
    // The files are already attached to the message being replayed.
    await runTurn({ ...turn, files: [], retried: true });
  }

  async function runTurn(turn: Turn) {
    lastTurn.value = turn;
    let turnClientId: string | null = null;

    isStreaming.value = true;
    streamingContent.value = "";
    rawSteps.value = [];

    try {
      await execute({
        cable: turn.cable,
        vueInstance: turn.vueInstance,
        channel: "AssistantChannel",
        httpCall: async (clientId: string) => {
          turnClientId = clientId;
          currentClientId.value = clientId;
          const { ok, conversationToken } = await postChat(turn, clientId);
          if (ok && conversationToken) currentConversationToken.value = conversationToken;
          if (!ok) failTurn("The assistant could not be reached. Your message was not sent.");
          return ok;
        },
        onMessage: (
          msg: CableMessage,
          { resolve, reject }: { resolve: (m: CableMessage) => void; reject: (e: Error) => void },
        ) => {
          // The user left the page mid-turn: this thread is gone, so its
          // remaining broadcasts must not land in the new one.
          if (turnClientId !== currentClientId.value) return resolve(msg);

          switch (msg.action) {
            case "delta":
              streamingContent.value = msg.text || "";
              break;
            case "activity":
              rawSteps.value.push({ kind: msg.kind, detail: msg.detail, ref: msg.ref, at: msg.at });
              break;
            case "activity_args": {
              const step = rawSteps.value.find((candidate) => candidate.ref === msg.ref && candidate.kind === "tool_call");
              if (step) {
                step.args = msg.args;
                step.done_at = msg.done_at;
              }
              break;
            }
            case "question":
              pendingQuestion.value = { questions: msg.questions ?? [], answers: null };
              break;
            case "complete":
              addMessage({
                role: "assistant",
                content: msg.text || streamingContent.value,
                id: msg.message_id ? String(msg.message_id) : undefined,
                steps: [...streamingSteps.value],
                stepsSummary: streamingSummary.value,
                question: pendingQuestion.value,
                programmingCheck: callsProgrammingCheck(rawSteps.value),
              });
              if (msg.mutated_tools?.length) {
                mutatedTools.value = msg.mutated_tools;
                mutatedAt.value = Date.now();
              }
              if (!open.value) hasUnread.value = true;
              lastTurn.value = null;
              resetStreamingState();
              loadConversations();
              resolve(msg);
              break;
            case "error":
              failTurn(msg.message || "An error occurred");
              reject(new Error(msg.message));
              break;
          }
        },
      });
    } catch {
      if (isStreaming.value) failTurn("The assistant stopped before answering.");
    }
  }

  // The partial answer is dropped: an error means the model never concluded,
  // and Retry replays the turn from the same question.
  function failTurn(message: string) {
    addMessage({ role: "error", content: message });
    resetStreamingState();
  }

  // The job polls the flag this raises, then closes the turn with whatever it
  // had streamed so far — so the UI keeps waiting for `complete`.
  async function stopGeneration() {
    if (!isStreaming.value || !currentClientId.value) return;

    await useMyFetch("/assistant/cancel").post({ client_id: currentClientId.value }).json();
  }

  // The answers are persisted on the message that asked, so a reload shows the
  // form as it was filled, then sent as the next turn for the model to act on.
  async function answerQuestion(
    messageId: string,
    text: string,
    answers: Record<string, AssistantAnswer>,
    cable: unknown,
    vueInstance: unknown,
  ) {
    const message = messages.value.find((candidate) => candidate.id === messageId);
    if (!message?.question || message.question.answers || !currentConversationToken.value) return;

    const { data } = await useMyFetch(
      `/assistant_conversations/${currentConversationToken.value}/messages/${messageId}`,
    )
      .put({ assistant_message: { question_answers: answers } })
      .json<{ question: AssistantQuestionForm | null }>();

    message.question = data.value?.question ?? { ...message.question, answers };
    await sendMessage(text, cable, vueInstance);
  }

  // Toggling the active feedback clears it, mirroring the Insight assistant.
  async function sendFeedback(messageId: string, feedback: "positive" | "negative") {
    const message = messages.value.find((candidate) => candidate.id === messageId);
    if (!message || !currentConversationToken.value) return;

    const value = message.feedback === feedback ? "no_opinion" : feedback;
    const { response } = await useMyFetch(
      `/assistant_conversations/${currentConversationToken.value}/messages/${messageId}`,
    )
      .put({ assistant_message: { user_feedback: value } })
      .json();

    if (response.value?.ok) message.feedback = value;
  }

  return {
    open,
    hasUnread,
    launcherState,
    toggle,
    openWith,
    queuedPrompt,
    takeQueuedPrompt,
    bindScope,
    loadScopeDefinitions,
    scopeDefinition,
    suggestionGroups,
    currentTitle,
    conversations,
    currentConversationToken,
    messages,
    sendFeedback,
    stopGeneration,
    isStreaming,
    streamingContent,
    streamingSteps,
    streamingSummary,
    pendingQuestion,
    lastTurn,
    mutatedAt,
    mutatedTools,
    loadConversations,
    openConversation,
    newConversation,
    deleteConversation,
    renameConversation,
    sendMessage,
    retryLastTurn,
    answerQuestion,
  };
});
